import ChevronRight from "/public/images/icons/chevronRight.svg?react";
import { useThemeContext } from "../../context/themeContext";
import { useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import IncidentIcon from "/public/images/icons/incidentIconWhite.svg?react";
import { useUserContext } from "../../context/userContext";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";
import { motion } from "framer-motion";

export default function PersonCard({ userData, checkinData, href, type, venueName }) {
  const [active, setActive] = useState(false);
  const [scanEvents, setScanEvents] = useState([]);
  const { theme } = useThemeContext();
  const { user, logout, updateTokens, selectedVenue } = useUserContext();
  let eventTime = new Date(userData?.WhenOpened).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  let status = null;
  
  if (type === "zone") {
    eventTime = new Date(userData?.WhenHappened).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (userData?.IsZoneChange) {
      status = "Zone Change";
    } else if (userData?.EventType === "CHECKIN") {
      status = "Checked In";
    } else if (userData?.EventType === "CHECKOUT") {
      status = "Checked Out";
    } else if (userData?.EventType === "UNLOCKBAND") {
      status = "Unlocked Band";
    } else if (userData?.EventType === "EMERGENCY") {
      status = "Emergency";
    }
  }

  const fetchScanEventsForParticipants = async () => {
    setScanEvents([]);
    
    if (active) {
      setActive(false);
      return;
    }

    await Sentry.startSpan(
      {
        name: "Fetch Scan Events",
        op: "http.request",
      },
      async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Scan/ScanEventsForParticipant?ParticipantId=${userData?.ParticipantId}&orgId=${selectedVenue.OrgID}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens
          );

          // Attach HTTP status to the span
          span.setAttribute("http.status_code", response.status);

          if (!response.ok) {
            span.setStatus("error"); // Mark as error
            throw new Error("Failed to fetch scan events");
          } else {
            const results = await response.json();
            setScanEvents(results);
            setActive(true);
            return results;
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error); // Log error to Sentry
          LogErrors(user, logout, updateTokens, "Error fetching scan events: " + error);
          return { error };
        } finally {
          span.end(); // Ensure span ends properly
        }
      }
    );
  };

  const cardContent = (
    <div
      className={`flex flex-col ${
        active
          ? theme === "staff"
            ? "border-[#1877D3]"
            : "border-[#EAAA00]"
          : "border-[#E3E3E3]"
      } bg-white border rounded-[8px] p-6 relative`}
    >
      {(userData?.HistoricalIncidentCount > 0 || userData?.EmergencyId) && (
        <div className="bg-sportsRed px-[12px] py-[7px] absolute left-0 top-[55px] transform -translate-y-1/2 rounded-r-[6px]">
          <IncidentIcon />
        </div>
      )}

      <div className="flex cursor-pointer flex-row w-full justify-between items-center">
        <div className="flex flex-row gap-y-3 gap-x-4 justify-center">
          {userData?.ProfilePicture ? (
            <img className='rounded-full border-[2px] border-[#EEEEF1] object-cover w-[62px] h-[62px]' src={userData?.ProfilePicture + `?t=${Date.now()}`} width="62" height="62" />
          ) : (
            <img src="/images/default-avatar.svg" width="62" height="62" />
          )}
          <div className="flex flex-col gap-2 justify-center">
            <p className="text-base font-medium text-black">
              {userData?.ParticipantName}
            </p>
            <p className="text-[#727272] font-poppins text-[16px] leading-[26px] capitalize">
              {checkinData ? (
                <>{userData?.ZoneName}</>
              ) : (
                type ? (
                  type === "emergency" ? (
                    <>{venueName} - {eventTime}</>
                  ) : type === "zone" ? (
                    <>{status} - {eventTime}</>
                  ) : null
                ) : null
              )}
            </p>
          </div>
        </div>
        <div className="">
          <ChevronRight
            className={` ${
              active
                ? theme === "staff"
                  ? "fill-[#1877D3] -rotate-90"
                  : "fill-[#EAAA00] -rotate-90"
                : "fill-black"
            } transition-all cursor-pointer ${checkinData && active && "rotate-90"}`}
          />
        </div>
      </div>
      {scanEvents && (
               <motion.div
               initial={false}
               animate={{
                 height: active ? "auto" : 0,
                 marginTop: active ? "24px" : 0,
               }}
               transition={{ duration: 0.5 }}
               className={`overflow-hidden px-2 border-dashed border-t border-transparent ${
                 active && "!border-[#E3E3E3]"
               }`}
             >
               <div className={`flex flex-col pt-[24px] gap-y-0`}>
               {scanEvents.sort((a, b) => new Date(b.WhenHappened) - new Date(a.WhenHappened)).map((data, index) => {
                 let eventLabel;
                 let eventClass;
                 let dotColor;
     
                 // Define the switch case based on EventType
                 if(data?.IsZoneChange){
                   eventLabel = "CHANGED ZONE";
                   eventClass = "bg-[#1877D3] text-white font-semibold";
                   dotColor = "bg-[#1877D3]";
                 } else if(data?.UserTriggering === "00000000-0000-0000-0000-000000000000") {
                   eventLabel = "Cleared Venue";
                   eventClass = "bg-[#EAAA00] text-white font-semibold";
                   dotColor = "bg-[#EAAA00]";
                 }  else {
                     switch (data.EventType) {
                     case "CHECKIN":
                       eventLabel = "Checked In";
                       eventClass = "bg-[#1877D3] text-white font-semibold";
                       dotColor = "bg-[#1877D3]";
                       break;
                     case "CHECKOUT":
                       if (data.ManualCheckout === 1) {
                       eventLabel = "Manually Checked Out";
                       eventClass = "bg-[#1877D3] text-white font-semibold";
                       dotColor = "bg-[#1877D3]";
                       } else {
                       eventLabel = "Checked Out";
                       eventClass = "bg-[#1877D3] text-white font-semibold";
                       dotColor = "bg-[#1877D3]";
                       }
                       break;
                     case "CHANGED_ZONE":
                       eventLabel = "Checked Out";
                       eventClass = "bg-[#1877D3] text-white font-semibold";
                       dotColor = "bg-[#1877D3]";
                       break;
                     case "EMERGENCY":
                       eventLabel = "Active Emergency";
                       eventClass = "bg-[#DF3327] text-white font-semibold";
                       dotColor = "bg-[#DF3327]";
                       break;
                     case "EMERGENCYCLOSED":
                       eventLabel = "Closed Emergency";
                       eventClass = "bg-[#6DB254] text-black font-semibold";
                       dotColor = "bg-[#6DB254]";
                       break;
                     case "ACTIVATEBAND":
                       eventLabel = "Band Activated";
                       eventClass = "bg-[#EAAA00] text-white font-semibold";
                       dotColor = "bg-[#EAAA00]";
                       break;
                     default:
                       eventLabel = data.EventType;
                       eventClass = "bg--[#1877D3] text-white font-semibold"; 
                       dotColor = "bg--[#1877D3]";
                       break;
                     }
                 }
     
                 return (
                   <div key={index} className={`relative flex flex-row items-center personCardWrapper ${checkinData ? '' : ' pb-0 gap-0'}`}>
                     {/* // Timeline Dot */}
                     <div className="relative h-[65px] min-w-[10px] flex justify-center pt-[9px] pb-[4px]">
                       <div className="border-l-[2px] border-[#E3E3E3] absolute left-[4px] h-full z-0 lastLine" />
                       <span className={`relative z-[2] w-[10px] h-[10px] rounded-full ${dotColor}`} />
                     </div>
     
                     {/* Event Information */}
                     <div className="ml-4">
                       <p className={`text-sm text-black ${eventClass} w-fit px-[10px] py-[5px] uppercase rounded-[4px] pb-[4px]`}>{eventLabel}</p>
                       <p className="text-sm pt-[4px]">
                         {new Date(data.WhenHappened).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                         {data.ZoneName && (
                           <span className={`px-2 py-1 rounded-lg`}>
                              • {data.ZoneName}
                           </span>
                         )}
                       </p>
                     </div>
                   </div>
                 );
               })}
               </div>
             </motion.div>
      )}
    </div>
  );

  if (checkinData) {
    return (
      <div onClick={() => fetchScanEventsForParticipants()}>
        {cardContent}
      </div>
    );
  } else if (href) {
    return (
      <Link to={href} state={{ userData }}>
        {cardContent}
      </Link>
    );
  } else {
    return (
      <div onClick={() => setActive(!active)}>
        {cardContent}
      </div>
    );
  }
}

PersonCard.propTypes = {
  userData: PropTypes.object.isRequired,
  checkinData: PropTypes.bool,
  href: PropTypes.string,
  type: PropTypes.string,
  venueName: PropTypes.string,
};